import { type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { getAdminProducts, type AdminProductStatus, type AdminProductSummary } from "@/lib/admin-api-client";
import { describeAdminError } from "@/components/admin/adminErrors";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<AdminProductStatus, string> = {
  DRAFT: "Brouillon",
  ACTIVE: "Actif",
  ARCHIVED: "Archivé",
};

/**
 * `/admin/catalogue/produits` — équivalent de
 * apps/web/app/(admin)/admin/catalogue/produits/page.tsx : liste paginée des
 * produits (DRAFT/ACTIVE/ARCHIVED inclus). Filtrage (recherche nom/SKU +
 * statut) et pagination pilotés par les paramètres d'URL — porté de
 * "formulaire GET natif" (Next.js) vers `useSearchParams()` de
 * react-router-dom, même principe (état entièrement dans l'URL, lien
 * partageable/rechargeable).
 */
export function AdminCatalogueProduitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get("search")?.trim() ?? "";
  const status = (searchParams.get("status") as AdminProductStatus | null) ?? undefined;
  const pageParam = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const { data: result, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "products", { search, status, page, pageSize: PAGE_SIZE }],
    queryFn: () =>
      getAdminProducts(undefined, {
        search: search || undefined,
        status: status || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: (prev) => prev,
  });

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const next = new URLSearchParams();
    const nextSearch = String(formData.get("search") ?? "").trim();
    const nextStatus = String(formData.get("status") ?? "").trim();
    if (nextSearch) next.set("search", nextSearch);
    if (nextStatus) next.set("status", nextStatus);
    setSearchParams(next);
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink">Produits</h1>
          <p className="mt-1 text-sm text-muted">{result ? `${result.totalItems} produit(s) au total.` : "…"}</p>
        </div>
        <Link
          to="/admin/catalogue/produits/nouveau"
          className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90"
        >
          Nouveau produit
        </Link>
      </div>

      {isError && (
        <p className="mt-4 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {describeAdminError(error)}
        </p>
      )}

      <form onSubmit={handleFilterSubmit} className="mt-6 flex flex-wrap items-end gap-4 rounded-sm border border-line bg-white p-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="search" className="text-xs font-medium text-muted">
            Recherche (nom, SKU)
          </label>
          <input
            id="search"
            name="search"
            type="text"
            defaultValue={search}
            placeholder="Nom du produit ou racine SKU…"
            className="w-64 rounded-sm border border-line px-3 py-2 text-sm text-ink"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="status" className="text-xs font-medium text-muted">
            Statut
          </label>
          <select
            id="status"
            name="status"
            defaultValue={status ?? ""}
            className="w-40 rounded-sm border border-line px-3 py-2 text-sm text-ink"
          >
            <option value="">Tous</option>
            <option value="DRAFT">Brouillon</option>
            <option value="ACTIVE">Actif</option>
            <option value="ARCHIVED">Archivé</option>
          </select>
        </div>

        <button type="submit" className="rounded-sm bg-navy px-4 py-2 text-sm font-medium text-cream hover:bg-navy/90">
          Filtrer
        </button>

        {(search || status) && (
          <Link to="/admin/catalogue/produits" className="pb-2 text-sm font-medium text-navy hover:underline">
            Réinitialiser
          </Link>
        )}
      </form>

      <div className="mt-6 overflow-x-auto rounded-sm border border-line bg-white">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-line text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Produit</th>
              <th className="px-4 py-3">SKU racine</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Marque</th>
              <th className="px-4 py-3">Catégorie</th>
              <th className="px-4 py-3 text-right">Prix</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                  Chargement…
                </td>
              </tr>
            )}
            {!isLoading && result && result.items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted">
                  Aucun produit ne correspond à ces critères.
                </td>
              </tr>
            )}
            {result?.items.map((product) => (
              <ProductRow key={product.id} product={product} />
            ))}
          </tbody>
        </table>
      </div>

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3 text-sm">
          <PageLink page={page - 1} disabled={page <= 1} search={search} status={status} label="← Précédent" />
          <span className="text-muted">
            Page {result.page} / {result.totalPages}
          </span>
          <PageLink
            page={page + 1}
            disabled={page >= result.totalPages}
            search={search}
            status={status}
            label="Suivant →"
          />
        </div>
      )}
    </div>
  );
}

function ProductRow({ product }: { product: AdminProductSummary }) {
  return (
    <tr className="border-b border-line last:border-0">
      <td className="px-4 py-3">
        <p className="font-medium text-ink">{product.name}</p>
        <p className="text-xs text-muted">{product.slug}</p>
      </td>
      <td className="px-4 py-3 font-mono text-xs text-muted">{product.skuRoot}</td>
      <td className="px-4 py-3">
        <span className="rounded-sm border border-line px-2 py-0.5 text-xs text-ink">
          {STATUS_LABEL[product.status]}
        </span>
      </td>
      <td className="px-4 py-3 text-ink">{product.brand?.name ?? "—"}</td>
      <td className="px-4 py-3 text-ink">{product.primaryCategory?.name ?? "—"}</td>
      <td className="px-4 py-3 text-right text-ink">{product.price?.formatted ?? "—"}</td>
      <td className="px-4 py-3 text-right">
        <Link to={`/admin/catalogue/produits/${product.id}`} className="text-sm font-medium text-navy hover:underline">
          Éditer
        </Link>
      </td>
    </tr>
  );
}

function PageLink({
  page,
  disabled,
  search,
  status,
  label,
}: {
  page: number;
  disabled: boolean;
  search: string;
  status: string | undefined;
  label: string;
}) {
  if (disabled) {
    return <span className="rounded-sm border border-line px-3 py-1.5 text-muted">{label}</span>;
  }

  const params = new URLSearchParams();
  params.set("page", String(page));
  if (search) params.set("search", search);
  if (status) params.set("status", status);

  return (
    <Link
      to={`/admin/catalogue/produits?${params.toString()}`}
      className="rounded-sm border border-navy px-3 py-1.5 text-navy hover:bg-navy hover:text-cream"
    >
      {label}
    </Link>
  );
}
